import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { ResponseIcon } from '../../support/response/ResponseActionButton';
import { responseTone, rsColor } from '../../support/response/responseScale';
import { SupportActionIcon } from '../../support/types';
import { OrbitL4ResultTone } from '../actionCard';
import { orbitRecordRoute } from '../routeMap';
import { OrbitActionLink } from '../types';

const LOOK: Record<
  OrbitL4ResultTone,
  { ink: string; fill: string; icon: SupportActionIcon | null }
> = {
  success: { ...responseTone('success'), icon: 'check' },
  pending: { ...responseTone('info'), icon: 'clock' },
  warning: { ...responseTone('warning'), icon: 'warning' },
  error: { ...responseTone('error'), icon: null }
};

// 6567:2429 / 6567:2477: centred, tinted 14 % and ruled in the tone; the error banner is drawn
// 0.8 px and radius 11 against the success banner's 1 px and 12.
const Banner = styled.section<{ $ink: string; $fill: string; $error: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  padding: 11px 5px;
  border: 1px solid ${({ $ink }) => $ink};
  border-radius: 12px;
  background: ${({ $fill }) => $fill};
  text-align: center;

  ${({ $error }) =>
    $error &&
    css`
      padding-bottom: 18px;
      border-width: 0.8px;
      border-radius: 11px;
    `}
`;

// 6567:2430: a 26 disc in the tone holding a 15 white glyph; the error's is the text glyph ✕.
const Disc = styled.span<{ $ink: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${({ $ink }) => $ink};
  color: ${rsColor.white};
  font-size: 16px;
  line-height: 19.2px;

  .anticon {
    font-size: 15px;
  }
`;

const Title = styled.p<{ $ink: string }>`
  margin: 11px 0 0;
  color: ${({ $ink }) => $ink};
  font-size: 12.5px;
  font-weight: 700;
  line-height: 15px;
  white-space: pre-line;
`;

const Badge = styled.span<{ $ink: string; $fill: string }>`
  margin-top: 6px;
  padding: 2px 9px;
  border: 1px solid ${({ $ink }) => $ink};
  border-radius: 999px;
  background: ${({ $fill }) => $fill};
  color: ${({ $ink }) => $ink};
  font-size: 10px;
  font-weight: 700;
  line-height: 15px;
`;

// 6567:2433: 35 tall, radius 9, padding 10 / 12, the open glyph trailing the label by 7.
const Cta = styled.button`
  display: inline-flex;
  align-items: center;
  height: 35px;
  margin-top: 13px;
  padding: 10px 12px;
  border: none;
  border-radius: 9px;
  background: ${rsColor.primary};
  color: ${rsColor.white};
  font-size: 12px;
  font-weight: 700;
  line-height: 15px;
  cursor: pointer;

  > * + * {
    margin-left: 7px;
  }

  &:hover,
  &:focus-visible {
    filter: brightness(1.1);
    outline: none;
  }
`;

interface IL4ResultBannerProps {
  tone: OrbitL4ResultTone;
  text: string;
  link: OrbitActionLink | null;
}

/** The end of an action; the record link sits beside the server's text, never inside it. */
const L4ResultBanner: React.FC<IL4ResultBannerProps> = ({ tone, text, link }) => {
  const { t } = useMultiLanguageTranslation();
  const navigate = useNavigate();
  const look = LOOK[tone];
  const route = orbitRecordRoute(link);

  return (
    <Banner
      $ink={look.ink}
      $fill={look.fill}
      $error={tone === 'error'}
      role="status"
      data-testid="orbit-l4-result"
      data-status={tone}
    >
      <Disc $ink={look.ink} aria-hidden="true">
        {look.icon === null ? '✕' : <ResponseIcon icon={look.icon} />}
      </Disc>
      <Title $ink={look.ink} data-testid="orbit-l4-text">
        {text}
      </Title>
      {tone === 'pending' ? (
        <Badge $ink={look.ink} $fill={look.fill} data-testid="orbit-l4-pending">
          {t(`${TOrbit}.layer4.pendingBadge`)}
        </Badge>
      ) : null}
      {route !== null && link !== null ? (
        <Cta type="button" data-testid="orbit-l4-link" onClick={() => navigate(route)}>
          <span>{t(`${TOrbit}.layer4.link.${link.entity}`)}</span>
          <ResponseIcon icon="open" />
        </Cta>
      ) : null}
    </Banner>
  );
};

export default L4ResultBanner;
